import type { FC } from 'react';
import { cn } from '../../utils/cn';

interface SkipLinkProps {
  /**
   * ID of the main content element to skip to (without the #)
   * @default 'main-content'
   */
  targetId?: string;
  /**
   * Text displayed in the skip link
   * @default 'Skip to main content'
   */
  label?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Skip link for keyboard navigation accessibility.
 * Allows keyboard users to bypass navigation and jump directly to main content.
 *
 * The link is visually hidden until focused, then appears prominently.
 *
 * Usage:
 * 1. Add <SkipLink /> at the top of your layout
 * 2. Add id="main-content" to your <main> element
 *
 * @example
 * ```tsx
 * // In your layout component:
 * <SkipLink />
 * <header>...</header>
 * <nav>...</nav>
 * <main id="main-content">...</main>
 * ```
 */
export const SkipLink: FC<SkipLinkProps> = ({
  targetId = 'main-content',
  label = 'Skip to main content',
  className,
}) => {
  return (
    <a
      href={`#${targetId}`}
      className={cn(
        // Visually hidden by default
        'sr-only',
        // When focused, show prominently
        'focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100]',
        'focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white',
        'focus:rounded-md focus:shadow-lg focus:font-medium',
        'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2',
        // Animation
        'transition-all duration-200',
        className
      )}
    >
      {label}
    </a>
  );
};

export default SkipLink;
